import {
    AppBar,
    Avatar,
    Box,
    Button,
    Chip,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Toolbar,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { ReactNode, type JSX } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomTransComponent } from './CustomTransComponent';

type CustomTabsProps = {
    initialTab?: string;
    tabsData: TabsDataType[];
    iconsData?: IconsDataType[];
    wrapperClass?: string;
    actions?: ReactNode;
    className?: string;
    type?: 'horizontal' | 'vertical';
    parentWrapper?: string;
    contentWrapper?: string;
    hideAppbar?: boolean;
    hideToolbar?: boolean;
    isSystemAdmin?: boolean;
    isEqualTabSize?: boolean;
    variableHeader?: boolean;
    hideShadow?: boolean;
    showExtraTabsInMenu?: boolean;
    onTabChange?: (args: any) => any;
    noHashRouting?: boolean;
    translationLoader?: boolean;
    subTab?: boolean;
};

export type IconsDataType = {
    component: ReactNode;
    onClick: () => void;
    className?: string;
    sx?: { [key: string]: unknown };
};

export type TabsDataType = {
    id: string;
    icon?: string | React.ReactElement;
    className?: string;
    sx?: { [key: string]: unknown };
    renderTitle: string | React.ReactNode;
    renderContent?: React.ReactNode;
    isDisabled?: boolean;
};

const resolveHashRoutingTab = (props: any, location: any, fallbackTabId: string): string | null => {
    const tabsData = props?.tabsData;
    const selectedTab = props?.selectedTab;
    const initialTab = props?.initialTab;
    const tabFromHash = location.hash.replace('#', '');
    const hashTabExists = tabsData?.some((tab: TabsDataType) => tab?.id === tabFromHash);

    if (tabFromHash && hashTabExists && tabFromHash !== selectedTab) {
        return tabFromHash;
    }

    const initialExists = tabsData?.some((tab: TabsDataType) => tab?.id === initialTab);
    const fallbackTab = (initialExists ? initialTab : fallbackTabId) ?? '';
    const noValidHash = !tabFromHash || !hashTabExists;

    if (noValidHash && fallbackTab && fallbackTab !== selectedTab) {
        return fallbackTab;
    }
    return null;
};

/* The `TabsComponent` function is a React component that renders the tabs and their associated
content. It takes in the `CustomTabsProps` as its props. */
const TabsComponent = React.memo((props: any): JSX.Element => {
    const theme: any = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const tabsContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [moreMenuEl, setMoreMenuEl] = React.useState<null | HTMLElement>(null);
    const [maxLength, setMaxLength] = React.useState<number>(props?.tabsData?.length);
    const handleClickMoreMenu = (event: any): void => setMoreMenuEl(event.currentTarget);
    const handleCloseMoreMenu = (): void => setMoreMenuEl(null);
    /**
     * Switches tab without discarding the context the screen was opened with.
     *
     * A hash-only `to` such as `'#Comments'` parses to an empty pathname *and an empty search*,
     * and carries no state, so `navigate('#Tab')` silently drops both the query string and
     * `location.state`. Screens that resolve their subject from that context then have to
     * re-derive it from the bare URL: Device Details, for example, is opened with the clicked
     * row's organization, and re-deriving it from the serial number alone resolves the wrong
     * device whenever a serial is not unique across organizations.
     */
    const navigateToTab = (tabId: string): void => {
        navigate({ hash: `#${tabId}`, search: location.search }, { state: location.state });
    };
    const getSelectedTabIndex = (): number =>
        props?.tabsData?.findIndex((tab: TabsDataType) => tab.id === props?.selectedTab);
    const getFallbackTabId = (): string => props?.tabsData?.find((tab: TabsDataType) => !!tab)?.id ?? '';
    const selectedTabExists = props?.tabsData?.some((tab: TabsDataType) => tab?.id === props?.selectedTab);
    const validSelectedTab = selectedTabExists ? props?.selectedTab : getFallbackTabId();
    React.useEffect(() => {
        if (props?.showExtraTabsInMenu) {
            const tabsWidth = tabsContainerRef.current?.getBoundingClientRect().width ?? props?.tabsData?.length;
            setMaxLength(tabsWidth / 250 - ((tabsWidth / 250) % 1) - 1);
        }
    }, []);

    React.useEffect(() => {
        if (props?.noHashRouting) {
            return;
        }

        const nextTab = resolveHashRoutingTab(props, location, getFallbackTabId());
        if (nextTab) {
            props.setSelectedTab(nextTab);
        }
    }, [location.hash, props?.initialTab, props?.tabsData, props?.selectedTab, props?.noHashRouting]);

    const tabsActions = React.useRef<any>(null);

    React.useEffect(() => {
        window.addEventListener('drawerWidthChanged', () => {
            tabsActions.current?.updateIndicator?.();
        });
    }, []);

    const getVisibleTabs = (): TabsDataType[] =>
        props?.tabsData?.slice(0, props?.type === 'vertical' ? props?.tabsData?.length : maxLength) ?? [];

    const renderTabItem = (tab: TabsDataType): JSX.Element | null => {
        if (props?.translationLoader) {
            return (
                <Stack key={tab?.id} spacing={2} mt={2}>
                    <Stack direction={'row'} spacing={2} alignItems={'center'}>
                        <Skeleton animation="wave" variant="circular" width={24} height={24} />
                        <Skeleton animation="wave" variant="text" width={150} height={34} />
                    </Stack>
                    <Skeleton animation="wave" variant="rectangular" width={200} height={2} />
                </Stack>
            );
        }

        if (!tab) return null;

        return (
            <Tab
                className={tab?.className}
                key={tab?.id}
                icon={tab?.icon}
                iconPosition={props?.type === 'vertical' ? 'end' : 'start'}
                label={tab?.renderTitle}
                data-testid={`tab-${tab?.id}`}
                {...(tab?.sx && {
                    sx: {
                        padding: '12px 16px',
                        ...tab?.sx,
                    },
                })}
                value={tab?.id}
                disabled={tab?.isDisabled}
            />
        );
    };

    const renderMoreMenuTrigger = (): JSX.Element =>
        getSelectedTabIndex() + 1 > maxLength ? (
            <Chip
                sx={{
                    backgroundColor: theme?.palette?.primary?.[50],
                    minHeight: '36px !important',
                    borderRadius: '18px !important',
                }}
                className="chip-avatar"
                onClick={handleClickMoreMenu}
                avatar={
                    <Avatar
                        sx={{ backgroundColor: `${theme?.palette?.primary?.[200]} !important` }}
                        className="height-36 width-36 margin-right-16"
                    >
                        <KeyboardDoubleArrowRight color="primary" data-testid="device-arrow-tab" />
                    </Avatar>
                }
                label={
                    <Box display="flex" flexDirection={'row'} sx={{ color: theme?.palette?.primary?.main }}>
                        {props?.tabsData?.[getSelectedTabIndex()]?.icon}
                        <Typography sx={{ fontSize: '16px', fontWeight: 400, ml: 1 }}>
                            {props?.tabsData?.[getSelectedTabIndex()]?.renderTitle}
                        </Typography>
                    </Box>
                }
            />
        ) : (
            <Button
                id="more"
                endIcon={<KeyboardDoubleArrowRight className="color-content" />}
                className="color-content"
                sx={{ px: 4, fontSize: '16px', fontWeight: 400 }}
                onClick={handleClickMoreMenu}
            >
                <CustomTransComponent translationKey={'COMMON:MORE_LABEL'} />
            </Button>
        );

    const renderMoreMenuItem = (tab: TabsDataType): JSX.Element | null => {
        if (!tab || props?.selectedTab === tab?.id) {
            return null;
        }
        const isSelected = tab?.id === props?.selectedTab;
        return (
            <MenuItem
                className={`${tab?.className ?? ''} ${isSelected ? '' : 'color-content'}`}
                key={tab?.id}
                {...(tab?.sx && {
                    sx: {
                        width: '100%', // changed from fixed '250px' to '100%' for flexible sizing
                        padding: '12px 16px',
                        ...tab?.sx,
                        ...(isSelected && {
                            color: theme?.palette?.primary?.main,
                        }),
                    },
                })}
                selected={isSelected}
                disabled={tab?.isDisabled}
                onClick={(): void => {
                    if (props?.noHashRouting) props.setSelectedTab(tab.id);
                    else navigateToTab(tab.id);
                    handleCloseMoreMenu();
                }}
            >
                {tab?.icon}
                <Box
                    ml={3}
                    sx={{
                        whiteSpace: 'normal', // added to allow wrapping
                        wordBreak: 'break-word', // added to break long translated words
                    }}
                >
                    {tab?.renderTitle}
                </Box>
            </MenuItem>
        );
    };

    const renderMoreTabsSection = (): JSX.Element | null => {
        if (!(props?.tabsData?.length > maxLength)) return null;
        return (
            <>
                {renderMoreMenuTrigger()}
                <Menu
                    id="moreMenuItems"
                    anchorEl={moreMenuEl}
                    open={Boolean(moreMenuEl)}
                    onClose={handleCloseMoreMenu}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                minWidth: '250px',
                                maxWidth: '350px',
                            },
                        },
                    }}
                    sx={{
                        '& .MuiList-root.MuiList-padding.MuiMenu-list': {
                            padding: 0,
                        },
                    }}
                >
                    {props?.tabsData?.slice(maxLength)?.map(renderMoreMenuItem)}
                </Menu>
            </>
        );
    };

    return (
        <>
            <Tabs
                ref={tabsContainerRef}
                orientation={props?.type ?? 'horizontal'}
                className={props?.wrapperClass ?? ''}
                value={validSelectedTab || false}
                onChange={(_event, tab: string): void => {
                    if (props?.noHashRouting) props.setSelectedTab(tab);
                    else navigateToTab(tab);
                }}
                variant={props?.isSystemAdmin && !props.isEqualTabSize ? 'fullWidth' : 'scrollable'}
                scrollButtons={false}
                indicatorColor="primary"
                textColor="inherit"
                action={(actions) => {
                    tabsActions.current = actions;
                }}
                sx={{
                    marginTop: props?.type === 'vertical' ? '24px' : '',
                    ...(props?.type !== 'vertical' && { width: '100%' }),
                    '& .MuiTabs-flexContainer': {
                        display: 'flex',
                        justifyContent: props.isEqualTabSize ? 'unset' : 'space-between',
                        alignItems: 'center',
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: props?.type === 'vertical' ? 'transparent' : theme?.palette?.primary?.main,
                    },
                    '& .Mui-selected': {
                        color: `${theme?.palette?.primary?.main} !important`,
                        fontWeight: '600',
                        fontSize: '14px',
                        background: props?.type === 'vertical' ? theme?.palette?.primary?.[50] : '',
                        '& .MuiSvgIcon-root': { color: theme?.palette?.primary?.main },
                    },
                    '& .MuiTab-root': {
                        color: '#424E54',
                        fontWeight: '400',
                        fontSize: '16px',
                        textTransform: 'none',
                    },
                    '& .MuiButtonBase-root': {
                        minHeight: '56px',
                    },
                }}
            >
                {getVisibleTabs().map((tab: TabsDataType) => renderTabItem(tab))}
                {props?.type === 'vertical' && props?.actions}
                {renderMoreTabsSection()}
                {/* Render icons on the right side of the tabs */}
                {props?.iconsData && (
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            justifyContent: 'flex-end', // Align icons to the right
                            alignItems: 'center',
                        }}
                    >
                        {props.iconsData.map(
                            (icon: { component: React.ReactNode; onClick: () => void }, index: number) => (
                                <Box
                                    key={index}
                                    onClick={icon.onClick}
                                    sx={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {icon.component}
                                </Box>
                            )
                        )}
                    </Stack>
                )}
            </Tabs>
            {props?.isSystemAdmin && (
                <div className="rotate-90 padding-12">
                    <MoreHorizIcon className="color-content" />
                </div>
            )}
            {props?.actions && props?.type !== 'vertical' && <Stack direction="row">{props.actions}</Stack>}
        </>
    );
});

/* The `renderTabComponent` function is a helper function that determines how to render the tabs
component based on the provided props. It takes in the `CustomTabsProps` as its argument and returns
a JSX element. */
const TabWrapperComponent = React.memo((props: any): JSX.Element => {
    if (props.hideAppbar && props.hideToolbar) {
        return <TabsComponent {...props} />;
    }

    if (props.hideAppbar) {
        return (
            <Toolbar className="min-height-56">
                <TabsComponent {...props} />
            </Toolbar>
        );
    }

    if (props.hideToolbar) {
        return (
            <AppBar
                sx={props?.hideShadow ? { boxShadow: 'none' } : {}}
                position={props?.actions ? 'static' : 'relative'}
                className="bg-white"
            >
                <TabsComponent {...props} />
            </AppBar>
        );
    }

    return (
        <AppBar
            position={props?.actions ? 'static' : 'relative'}
            className={`bg-white`}
            ref={props?.appbarRef}
            sx={{
                '&.MuiPaper-root.MuiAppBar-root': {
                    zIndex: 1000,
                    position: 'sticky',
                    left: 'auto',
                    right: 0,
                    top: '56px !important',
                },
                ...(props?.hideShadow ? { boxShadow: 'none' } : {}),
            }}
        >
            <Toolbar className="min-height-56">
                <TabsComponent {...props} />
            </Toolbar>
        </AppBar>
    );
});

const resolveInitialTab = (props: any, location: any): string => {
    if (!props?.noHashRouting) {
        return location.hash.replace('#', '') ?? '';
    }
    if (props?.subTab) {
        return location?.state?.subTab ?? props?.initialTab ?? props?.tabsData?.[0]?.id ?? '';
    }
    return props?.initialTab ?? props?.tabsData?.[0]?.id ?? '';
};

const applyVariableHeaderTop = (appbarRef: any): void => {
    const headerElement = document.getElementById('app-collapsible-header');
    if (headerElement && appbarRef.current) {
        const variableHeight = headerElement?.getBoundingClientRect().height;
        appbarRef.current.style.top = `${variableHeight}px`;
    }
};

const applyScrollHeaderEffect = (props: any, appbarRef: any): (() => void) | void => {
    window.scrollTo({ top: 0 });
    if (!appbarRef.current) {
        return;
    }
    if (props?.variableHeader) {
        const handler = (): void => applyVariableHeaderTop(appbarRef);
        window.addEventListener('scroll', handler);
        return () => window.removeEventListener('scroll', handler);
    }
    appbarRef.current.style.top = `${appbarRef.current.getBoundingClientRect()?.top}px`;
};

const ensureValidSelectedTab = (props: any, selectedTab: string, setSelectedTab: (id: string) => void): void => {
    if (!props?.tabsData?.length) {
        return;
    }
    const isValid = props?.tabsData?.some((tab: TabsDataType) => tab?.id === selectedTab);
    if (!isValid) {
        setSelectedTab(props?.initialTab || props?.tabsData?.[0]?.id || '');
    }
};

/* The code is exporting a React component called `CustomTabs` using the `export` keyword. The
component is created using the `React.memo` function, which is a higher-order component that
memoizes the component to optimize performance by preventing unnecessary re-renders. */
export const CustomTabs = React.memo((props: CustomTabsProps): JSX.Element => {
    const location = useLocation();

    const [selectedTab, setSelectedTab] = React.useState<string>(() => resolveInitialTab(props, location));
    const selectedTabData = props?.tabsData?.find((tab) => tab?.id === selectedTab);
    const appbarRef: any = React.useRef(null);

    React.useLayoutEffect(() => applyScrollHeaderEffect(props, appbarRef), []);
    React.useEffect(() => {
        props?.onTabChange?.(selectedTab);
    }, [selectedTab]);

    React.useEffect(() => {
        ensureValidSelectedTab(props, selectedTab, setSelectedTab);
    }, [props?.tabsData, props?.initialTab, selectedTab]);

    return (
        <Box className={`${props.parentWrapper ?? ' '}`}>
            <TabWrapperComponent
                {...props}
                setSelectedTab={setSelectedTab}
                selectedTab={selectedTab}
                appbarRef={appbarRef}
            />
            <Box className={`w-100 ${props.contentWrapper ?? ' '}`}>{selectedTabData?.renderContent}</Box>
        </Box>
    );
});
